// src/pages/time/CalendarPage.jsx
// Extracted from App.js - Calendar view
// BRIO Migration: Using code "5a" (table dense + striped)
import React from 'react';
import { Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  BrioAnalyticsSurface,
  BrioFrame
} from '../../components/BrioComponents';

// Calendar category colors — drawn from the BRIO team palette
// (DEFAULT_TEAM_PALETTE in AccountingSettingsPage.jsx). Keep in sync with
// the colorMap in App.js getBrioEventStyle so filter buttons and event
// boxes share the same hue per category.
// Hue families:
//   leaves + pendingLeaves    → yellow-brown (gold / ochre)
//   birthdays + anniversaries → cool purple  (lavender / muted purple)
//   clientBirthdays + clientAnniversaries → warm pink (mauve / plum)
// Standalone (each clearly distinct from every other family):
//   holidays → red, milestones → forest green, invoicing → blue steel,
//   events → teal, msCalendar → warm grey
export const CALENDAR_CATEGORY_COLORS = {
  holidays: '#B36B6B',           // muted red
  leaves: '#CC9966',             // tan / gold
  pendingLeaves: '#9B8B6B',      // ochre (sibling of leaves)
  milestones: '#6B9B7B',         // forest green
  invoicing: '#5B7A99',          // blue steel (financial / distinct from brown family)
  events: '#6B8B8B',             // teal (clearly outside the purple range)
  anniversaries: '#8B7BA3',      // muted purple (team-celebration family)
  msCalendar: '#9B8B7A',         // warm grey
  birthdays: '#7A6B8B',          // lavender (team-celebration family)
  clientBirthdays: '#9B7A8B',    // mauve (client-celebration family)
  clientAnniversaries: '#8B6B7A' // plum (client-celebration family)
};

// Common button style for category filters
const filterButtonStyle = {
  fontSize: '10px',
  padding: '2px 5px',
  lineHeight: 1.2,
  height: '26px',
  borderRadius: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--brio-white)',
  whiteSpace: 'nowrap',
  flexShrink: 0
};

export default function CalendarPage({
  // Data
  currentUser,
  calView,
  calDate,
  categoryFilters,
  msCalendarConnected,
  msSyncInProgress,
  // State setters
  setCalView,
  setCalDate,
  setCategoryFilters,
  setEventForm,
  setShowEventModal,
  // Calendar helpers (kept in App.js, passed as props)
  renderWeeklyView,
  renderMonthlyView,
  getDaysInView,
  // MS Calendar sync (optional, for manual sync trigger)
  syncMsCalendar,
}) {
  return (
    <div className="brio-flex brio-flex-col brio-analytics-font" style={{ background: '#fff' }}>
      {/* PAGE HEADER BAND: Full-width white background, centered content */}
      <div className="brio-pageband brio-pageband--header">
        <BrioFrame>
          <div className="brio-page-header__content">
            <div className="brio-page-header__left">

            </div>
            <div className="brio-page-header__right">
              <div className="brio-flex brio-gap-xs">
                <button 
                  onClick={() => setCalView('week')} 
                  className={`brio-btn ${calView === 'week' ? 'brio-btn-primary' : 'brio-btn-secondary'}`}
                  style={{ borderRadius: 0 }}
                >
                  Week
                </button>
                <button 
                  onClick={() => setCalView('month')} 
                  className={`brio-btn ${calView === 'month' ? 'brio-btn-primary' : 'brio-btn-secondary'}`}
                  style={{ borderRadius: 0 }}
                >
                  Month
                </button>
              </div>
            </div>
          </div>
        </BrioFrame>
      </div>

      {/* CONTENT AREA */}
      <BrioFrame>
        <div className="brio-flex brio-flex-col brio-gap-s" style={{ paddingTop: 'var(--brio-space-s)' }}>
          {/* Category Filters */}
          <BrioAnalyticsSurface padding="s">
            <div className="brio-flex brio-items-center" style={{ gap: '4px', flexWrap: 'nowrap', overflowX: 'auto' }}>
              {/* Holidays Toggle */}
              <button
                onClick={() => setCategoryFilters({...categoryFilters, holidays: !categoryFilters.holidays})}
                className={`brio-btn ${categoryFilters.holidays ? 'brio-btn-primary' : 'brio-btn-secondary'}`}
                style={categoryFilters.holidays 
                  ? { ...filterButtonStyle, background: CALENDAR_CATEGORY_COLORS.holidays, borderColor: CALENDAR_CATEGORY_COLORS.holidays } 
                  : filterButtonStyle}
              >
                Holidays
              </button>
            
              {/* Approved Leaves Toggle */}
              <button
                onClick={() => setCategoryFilters({...categoryFilters, leaves: !categoryFilters.leaves})}
                className={`brio-btn ${categoryFilters.leaves ? 'brio-btn-primary' : 'brio-btn-secondary'}`}
                style={categoryFilters.leaves 
                  ? { ...filterButtonStyle, background: CALENDAR_CATEGORY_COLORS.leaves, borderColor: CALENDAR_CATEGORY_COLORS.leaves } 
                  : filterButtonStyle}
              >
                Team Leaves
              </button>
            
              {/* Pending Leaves Toggle - Admin/Supervisor only */}
              {(currentUser.role === 'admin' || currentUser.role === 'admin-pa' || currentUser.role === 'supervisor') && (
                <button
                  onClick={() => setCategoryFilters({...categoryFilters, pendingLeaves: !categoryFilters.pendingLeaves})}
                  className={`brio-btn ${categoryFilters.pendingLeaves ? 'brio-btn-primary' : 'brio-btn-secondary'}`}
                  style={categoryFilters.pendingLeaves 
                    ? { ...filterButtonStyle, background: CALENDAR_CATEGORY_COLORS.pendingLeaves, borderColor: CALENDAR_CATEGORY_COLORS.pendingLeaves } 
                    : filterButtonStyle}
                >
                  Pending Leaves
                </button>
              )}
            
              {(currentUser.role === 'admin' || currentUser.role === 'admin-pa' || currentUser.role === 'supervisor') && (
                <>
                  <button
                    onClick={() => setCategoryFilters({...categoryFilters, milestones: !categoryFilters.milestones})}
                    className={`brio-btn ${categoryFilters.milestones ? 'brio-btn-primary' : 'brio-btn-secondary'}`}
                    style={categoryFilters.milestones 
                      ? { ...filterButtonStyle, background: CALENDAR_CATEGORY_COLORS.milestones, borderColor: CALENDAR_CATEGORY_COLORS.milestones } 
                      : filterButtonStyle}
                  >
                    Milestones
                  </button>
                
                  <button
                    onClick={() => setCategoryFilters({...categoryFilters, invoicing: !categoryFilters.invoicing})}
                    className={`brio-btn ${categoryFilters.invoicing ? 'brio-btn-primary' : 'brio-btn-secondary'}`}
                    style={categoryFilters.invoicing 
                      ? { ...filterButtonStyle, background: CALENDAR_CATEGORY_COLORS.invoicing, borderColor: CALENDAR_CATEGORY_COLORS.invoicing } 
                      : filterButtonStyle}
                  >
                    Invoicing
                  </button>
                </>
              )}
            
              <button
                onClick={() => setCategoryFilters({...categoryFilters, events: !categoryFilters.events})}
                className={`brio-btn ${categoryFilters.events ? 'brio-btn-primary' : 'brio-btn-secondary'}`}
                style={categoryFilters.events 
                  ? { ...filterButtonStyle, background: CALENDAR_CATEGORY_COLORS.events, borderColor: CALENDAR_CATEGORY_COLORS.events } 
                  : filterButtonStyle}
              >
                Events
              </button>
            
              {/* Work Anniversaries Toggle */}
              <button
                onClick={() => setCategoryFilters({...categoryFilters, anniversaries: !categoryFilters.anniversaries})}
                className={`brio-btn ${categoryFilters.anniversaries ? 'brio-btn-primary' : 'brio-btn-secondary'}`}
                style={categoryFilters.anniversaries
                  ? { ...filterButtonStyle, background: CALENDAR_CATEGORY_COLORS.anniversaries, borderColor: CALENDAR_CATEGORY_COLORS.anniversaries }
                  : filterButtonStyle}
              >
                Anniversaries
              </button>

              {/* Birthdays Toggle */}
              <button
                onClick={() => setCategoryFilters({...categoryFilters, birthdays: !categoryFilters.birthdays})}
                className={`brio-btn ${categoryFilters.birthdays ? 'brio-btn-primary' : 'brio-btn-secondary'}`}
                style={categoryFilters.birthdays
                  ? { ...filterButtonStyle, background: CALENDAR_CATEGORY_COLORS.birthdays, borderColor: CALENDAR_CATEGORY_COLORS.birthdays }
                  : filterButtonStyle}
              >
                Birthdays
              </button>

              {/* Client Birthdays Toggle */}
              <button
                onClick={() => setCategoryFilters({...categoryFilters, clientBirthdays: !categoryFilters.clientBirthdays})}
                className={`brio-btn ${categoryFilters.clientBirthdays ? 'brio-btn-primary' : 'brio-btn-secondary'}`}
                style={categoryFilters.clientBirthdays
                  ? { ...filterButtonStyle, background: CALENDAR_CATEGORY_COLORS.clientBirthdays, borderColor: CALENDAR_CATEGORY_COLORS.clientBirthdays }
                  : filterButtonStyle}
              >
                Client Birthdays
              </button>

              {/* Client Anniversaries Toggle */}
              <button
                onClick={() => setCategoryFilters({...categoryFilters, clientAnniversaries: !categoryFilters.clientAnniversaries})}
                className={`brio-btn ${categoryFilters.clientAnniversaries ? 'brio-btn-primary' : 'brio-btn-secondary'}`}
                style={categoryFilters.clientAnniversaries
                  ? { ...filterButtonStyle, background: CALENDAR_CATEGORY_COLORS.clientAnniversaries, borderColor: CALENDAR_CATEGORY_COLORS.clientAnniversaries }
                  : filterButtonStyle}
              >
                Client Anniv.
              </button>

              {/* MS Calendar Toggle */}
              {msCalendarConnected && (
                <button
                  onClick={() => {
                    const newValue = !categoryFilters.msCalendar;
                    setCategoryFilters({...categoryFilters, msCalendar: newValue});
                    // Note: Auto-sync is handled by useEffect in App.js when filter is enabled
                  }}
                  disabled={msSyncInProgress}
                  className={`brio-btn ${categoryFilters.msCalendar ? 'brio-btn-primary' : 'brio-btn-secondary'}`}
                  style={categoryFilters.msCalendar 
                    ? { ...filterButtonStyle, background: CALENDAR_CATEGORY_COLORS.msCalendar, borderColor: CALENDAR_CATEGORY_COLORS.msCalendar, opacity: msSyncInProgress ? 0.7 : 1 } 
                    : { ...filterButtonStyle, opacity: msSyncInProgress ? 0.7 : 1 }}
                >
                  {msSyncInProgress ? 'Syncing...' : 'MS Calendar'}
                </button>
              )}
            
              <button
                onClick={() => {
                  const now = new Date();
                  const currentTime = now.toTimeString().slice(0, 5);
                  const oneHourLater = new Date(now.getTime() + 3600000).toTimeString().slice(0, 5);

                  setEventForm({
                    isAllDay: false,
                    date: now.toISOString().split('T')[0],
                    startDate: now.toISOString().split('T')[0],
                    endDate: now.toISOString().split('T')[0],
                    startTime: currentTime,
                    endTime: oneHourLater,
                    category: 'events',
                    title: '',
                    description: ''
                  });
                  setShowEventModal(true);
                }}
                className="brio-btn brio-btn-primary"
                style={{ ...filterButtonStyle, marginLeft: 'auto', gap: '4px' }}
              >
                <Plus className="w-3 h-3"/>
                Add Event
              </button>
            </div>
          </BrioAnalyticsSurface>

          {/* Navigation */}
          <BrioAnalyticsSurface padding="s">
            <div className="brio-flex brio-items-center brio-justify-between">
              <button 
                onClick={() => {
                  const days = calView === 'week' ? 7 : 30;
                  setCalDate(new Date(calDate.getTime() - days * 86400000));
                }} 
                className="brio-btn brio-btn-secondary"
              >
                <ChevronLeft className="w-5 h-5"/>
              </button>
            
              <div className="brio-flex brio-items-center brio-gap-s">
                <button
                  onClick={() => setCalDate(new Date())}
                  className="brio-btn brio-btn-secondary"
                >
                  Today
                </button>
                <div className="brio-analytics-body brio-text-xl brio-font-medium">
                  {calDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </div>
              </div>
            
              <button 
                onClick={() => {
                  const days = calView === 'week' ? 7 : 30;
                  setCalDate(new Date(calDate.getTime() + days * 86400000));
                }} 
                className="brio-btn brio-btn-secondary"
              >
                <ChevronRight className="w-5 h-5"/>
              </button>
            </div>
          </BrioAnalyticsSurface>

          {/* Calendar Views */}
          {calView === 'week' ? renderWeeklyView(getDaysInView()) : renderMonthlyView(getDaysInView())}
        </div>
      </BrioFrame>
    </div>
  );
}
